import { useEffect, type RefObject } from 'react';

import { WIZARD_ACTIVE_HTML_ATTRIBUTE } from './constants';

const BLOCKED_EVENTS = ['mousedown', 'click', 'touchstart'] as const;

function getPointerClientPoint(
  event: Event,
): { x: number; y: number } | null {
  if (event instanceof MouseEvent) {
    return { x: event.clientX, y: event.clientY };
  }
  if (event instanceof TouchEvent && event.touches.length > 0) {
    const touch = event.touches[0];
    return { x: touch.clientX, y: touch.clientY };
  }
  return null;
}

function isPointerWithinCard(
  event: Event,
  cardElement: HTMLDivElement,
): boolean {
  const target = event.target;
  if (target instanceof Node && cardElement.contains(target)) {
    return true;
  }

  const point = getPointerClientPoint(event);
  if (!point) {
    return false;
  }

  const rect = cardElement.getBoundingClientRect();
  return (
    point.x >= rect.left &&
    point.x <= rect.right &&
    point.y >= rect.top &&
    point.y <= rect.bottom
  );
}

/** Marks the document while a wizard runs and blocks background pointer input. */
export function useWizardInteractionLock(
  active: boolean,
  cardRef: RefObject<HTMLDivElement | null>,
): void {
  useEffect(() => {
    if (!active) {
      return;
    }

    document.documentElement.setAttribute(WIZARD_ACTIVE_HTML_ATTRIBUTE, '');
    return () => {
      document.documentElement.removeAttribute(WIZARD_ACTIVE_HTML_ATTRIBUTE);
    };
  }, [active]);

  useEffect(() => {
    if (!active) {
      return;
    }

    const blockBackgroundInteraction = (event: Event) => {
      const cardElement = cardRef.current;
      if (cardElement && isPointerWithinCard(event, cardElement)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
    };

    for (const eventType of BLOCKED_EVENTS) {
      document.addEventListener(eventType, blockBackgroundInteraction, true);
    }

    return () => {
      for (const eventType of BLOCKED_EVENTS) {
        document.removeEventListener(
          eventType,
          blockBackgroundInteraction,
          true,
        );
      }
    };
  }, [active, cardRef]);
}
